import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'; 
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

const container = document.getElementById('viewer-container');
const canvas = document.querySelector('canvas.webgl');

// --- SCENE ---
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xf4f4f4); 

const renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: false });
renderer.setSize(container.clientWidth, container.clientHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;

// --- ENVIRONMENT ---
const pmremGenerator = new THREE.PMREMGenerator(renderer);
scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;

// --- TEXTURES ---
const textureLoader = new THREE.TextureLoader();

// 1. LEATHER TEXTURES
const leatherNormal = textureLoader.load('/textures/leather/Leather034C_1K-PNG_NormalGL.png');
const leatherRoughness = textureLoader.load('/textures/leather/Leather034C_1K-PNG_Roughness.png');
const leatherAO = textureLoader.load('/textures/leather/Leather034C_1K-PNG_AmbientOcclusion.png');

[leatherNormal, leatherRoughness, leatherAO].forEach(t => {
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(4, 4); // Adjusted for scale
});

// 2. METAL TEXTURES (For Arms/Base)
// We skip the Color map to keep it Black
const metalNormal = textureLoader.load('/textures/metal/Metal028_1K-PNG_NormalGL.png');
const metalRoughness = textureLoader.load('/textures/metal/Metal028_1K-PNG_Roughness.png');
const metalMetalness = textureLoader.load('/textures/metal/Metal028_1K-PNG_Metalness.png');

[metalNormal, metalRoughness, metalMetalness].forEach(t => {
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(2, 2); // Tiling for the metal grain
});

// --- MATERIALS ---

// 1. Leather (Dynamic Color)
const leatherMat = new THREE.MeshStandardMaterial({
    color: 0x111111, // Start Black
    normalMap: leatherNormal,
    normalScale: new THREE.Vector2(0.6, 0.6),
    roughnessMap: leatherRoughness,
    aoMap: leatherAO,
    roughness: 0.6,
    metalness: 0.1
});

// 2. Textured Black Metal (Arms & Base)
const texturedBlackMat = new THREE.MeshStandardMaterial({
    color: 0x111111, // Deep Black Base
    normalMap: metalNormal,
    normalScale: new THREE.Vector2(0.5, 0.5), // Subtle surface detail
    roughnessMap: metalRoughness,
    metalnessMap: metalMetalness,
    roughness: 1.0, 
    metalness: 1.0 // Fully metallic interaction
});

// 3. Simple Chrome (Gas Lift)
const chromeMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.1,
    metalness: 1.0
});

// --- GEOMETRY ---
const chair = new THREE.Group();

// 1. BASE
const base = new THREE.Group();
for(let i=0; i<5; i++) {
    const angle = (i/5)*Math.PI*2;
    const leg = new THREE.Group();
    leg.rotation.y = angle;
    
    // Leg with Textured Metal
    const lGeo = new THREE.CylinderGeometry(0.045, 0.025, 0.32, 16);
    lGeo.translate(0, -0.16, 0); lGeo.rotateX(-Math.PI/2); lGeo.scale(1, 0.7, 1);
    const lMesh = new THREE.Mesh(lGeo, texturedBlackMat);
    lMesh.position.z = 0.06; lMesh.castShadow = true; lMesh.receiveShadow = true;
    leg.add(lMesh);
    
    // Caster
    const c = new THREE.Group(); c.position.set(0, -0.035, 0.36);
    const wGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.05, 16); wGeo.rotateZ(Math.PI/2);
    const w1 = new THREE.Mesh(wGeo, texturedBlackMat); w1.position.x = 0;
    const w2 = new THREE.Mesh(wGeo, texturedBlackMat); w2.position.x = 0;
    c.add(w1, w2);
    leg.add(c);
    base.add(leg);
}
// Hub & Lift
base.add(new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.1, 16), texturedBlackMat));
const lift = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.4, 16), chromeMat);
lift.position.y = 0.20;
base.add(lift);
base.position.y = 0.08; chair.add(base);

// 2. SEAT & MECH
const seatH = 0.5;
// Mechanism (Using textured mat for consistency)
const mech = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.08, 0.24), texturedBlackMat);
mech.position.y = 0.40; chair.add(mech);

const seat = new THREE.Mesh(new RoundedBoxGeometry(0.55, 0.12, 0.52, 4, 0.03), leatherMat);
seat.position.y = 0.49; seat.castShadow = true; seat.receiveShadow = true; chair.add(seat);

// 3. SPINE & BACK
const spineGroup = new THREE.Group();
spineGroup.add(new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.02, 0.20), texturedBlackMat).translateX(0).translateY(0.40).translateZ(-0.15));
const spineV = new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.70, 0.02), texturedBlackMat);
spineV.position.set(0, 0.75, -0.25);
spineV.rotation.x = -0.15;
spineGroup.add(spineV);
chair.add(spineGroup);

const back = new THREE.Mesh(new RoundedBoxGeometry(0.50, 0.55, 0.08, 4, 0.03), leatherMat);
back.position.set(0, 0.75, -0.27); back.rotation.x = -0.15; back.castShadow = true; chair.add(back);

const head = new THREE.Mesh(new RoundedBoxGeometry(0.50, 0.20, 0.09, 4, 0.03), leatherMat);
head.position.set(0, 1.08, -0.33); head.rotation.x = -0.15; head.castShadow = true; chair.add(head);

// 4. ARMRESTS (Textured Black Metal)
function createArm(side) {
    const g = new THREE.Group();
    // Connector
    const conn = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.30), texturedBlackMat);
    conn.rotateZ(Math.PI/2);
    conn.position.set(side * 0.20, 0, 0);

    // Post
    const postH = 0.26;
    const post = new THREE.Mesh(new RoundedBoxGeometry(0.04, postH, 0.04, 2, 0.005), texturedBlackMat);
    post.position.set(side * 0.35, postH/2 - 0.02, -0.035); 
    post.rotation.x = -0.35; 

    // Pad
    const padH = postH - 0.02
    const pad = new THREE.Mesh(new RoundedBoxGeometry(0.07, 0.03, 0.35, 4, 0.01), texturedBlackMat);
    pad.position.set(side * 0.35, padH, 0.08);

    g.add(conn, post, pad);
    g.position.y = 0.40; 
    return g;
}
chair.add(createArm(1)); chair.add(createArm(-1));
scene.add(chair);

// --- LIGHTING ---
scene.add(new THREE.AmbientLight(0xffffff, 0.7));
const mainL = new THREE.DirectionalLight(0xffffff, 1.5); mainL.position.set(5, 8, 5); mainL.castShadow = true; scene.add(mainL);
scene.add(new THREE.DirectionalLight(0xddeeff, 0.8).position.set(-5, 2, -5));

// --- CAMERA ---
const camera = new THREE.PerspectiveCamera(40, container.clientWidth/container.clientHeight, 0.1, 100);
camera.position.set(2.5, 1.5, 3);
const controls = new OrbitControls(camera, canvas);
controls.target.set(0, 0.6, 0); controls.enableDamping = true; controls.enablePan = false;

// --- LISTENERS ---
window.addEventListener('resize', () => {
    const w = container.clientWidth, h = container.clientHeight;
    camera.aspect = w/h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
});

window.addEventListener('colorChange', (e) => {
    leatherMat.color.setHex(e.detail);
});

function tick() { controls.update(); renderer.render(scene, camera); requestAnimationFrame(tick); }
tick();